"use client";

import { useEffect, useRef, useState } from 'react';
import type { ImgHTMLAttributes, SyntheticEvent } from 'react';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/shared/lib/utils';

import DEFAULT_ERROR_PLACEHOLDER_SRC from '@/assets/images/ic_ai_error.svg';

type EImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt' | 'onLoad' | 'onError' | 'className'> & {
    src: string;
    alt: string;
    className?: string;
    imageClassName?: string;
    skeletonClassName?: string;
    errorPlaceholderSrc?: string;
    errorPlaceholderAlt?: string;
    onLoad?: ImgHTMLAttributes<HTMLImageElement>['onLoad'];
    onError?: ImgHTMLAttributes<HTMLImageElement>['onError'];
    children?: React.ReactNode;
};

export function EImage({
    src,
    alt,
    className,
    imageClassName,
    skeletonClassName,
    errorPlaceholderSrc = DEFAULT_ERROR_PLACEHOLDER_SRC,
    errorPlaceholderAlt = '图片加载失败',
    onLoad,
    onError,
    children,
    ...props
}: EImageProps) {
    const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
    const [isSkeletonMounted, setIsSkeletonMounted] = useState(true);
    const currentSrcRef = useRef(src);

    if (currentSrcRef.current !== src) {
        currentSrcRef.current = src;
        setStatus('loading');
        setIsSkeletonMounted(true);
    }

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (status === 'loaded' || status === 'error') {
            timer = setTimeout(() => setIsSkeletonMounted(false), 300);
        } else {
            setIsSkeletonMounted(true);
        }
        return () => clearTimeout(timer);
    }, [status]);

    const wrapperClassName = cn(
        'relative inline-flex overflow-hidden',
        className,
    );
    const imageStateClassName = cn('block h-full w-full object-cover', imageClassName);
    const skeletonClassNameValue = cn('absolute inset-0 h-full w-full rounded-[inherit] bg-[#1F1F1F]', skeletonClassName);

    const handleLoad = (event: SyntheticEvent<HTMLImageElement>) => {
        setStatus('loaded');
        onLoad?.(event);
    };

    const handleError = (event: SyntheticEvent<HTMLImageElement>) => {
        setStatus('error');
        onError?.(event);
    };

    const imageProps = {
        ...props,
        className: imageStateClassName,
    };

    return (
        <span className={wrapperClassName}>
            {isSkeletonMounted && (
                <div
                    className={cn(
                        'absolute inset-0 transition-opacity duration-300',
                        status === 'loaded' || status === 'error' ? 'opacity-0 pointer-events-none' : 'opacity-100',
                    )}
                    aria-hidden="true"
                >
                    <Skeleton className={skeletonClassNameValue} />
                </div>
            )}

            {status === 'error' ? (
                <div className={cn("flex h-full w-full items-center justify-center bg-[#1F1F1F]", imageClassName)}>
                    <img src={errorPlaceholderSrc} alt={errorPlaceholderAlt} className="h-[60%] w-[60%] object-contain" />
                </div>
            ) : (
                <img
                    key={src}
                    {...imageProps}
                    src={src}
                    alt={alt}
                    className={cn(
                        imageStateClassName,
                        'transition-opacity duration-300',
                        status === 'loaded' ? 'opacity-100' : 'opacity-0',
                    )}
                    onLoad={handleLoad}
                    onError={handleError}
                />
            )}
            {children}
        </span>
    );
}
